
import { useState } from 'react';

interface MembershipUpgradeProps {
  currentPlan: string;
  currentDiscount: number;
  currentLimit: number;
  totalItems: number;
  overLimitPieces: number;
  cartItems: { service: string; basePrice: number; quantity: number }[];
  onUpgradeInCart?: (
    planName: string,
    discount: number,
    limit: number,
    upgradePrice: number,
  ) => void;
  mode?: 'over-limit' | 'approaching-limit';
}

const PLANS = [
  {
    name: 'BASIC',
    limit: 10,
    discount: 15,
    price: 599,
    deliveryHours: 72,
    freeShipping: false,
    freeWeekend: false,
    shippingCost: 399,
  },
  {
    name: 'PREMIUM',
    limit: 20,
    discount: 20,
    price: 999,
    deliveryHours: 48,
    freeShipping: false,
    freeWeekend: false,
    shippingCost: 399,
  },
  {
    name: 'VIP',
    limit: 30,
    discount: 30,
    price: 1799,
    deliveryHours: 24,
    freeShipping: true,
    freeWeekend: true,
    shippingCost: 0,
  },
];

export default function MembershipUpgrade({
  currentPlan,
  currentDiscount,
  currentLimit,
  totalItems,
  overLimitPieces,
  cartItems,
  onUpgradeInCart,
  mode = 'over-limit',
}: MembershipUpgradeProps) {
  const [dismissed, setDismissed] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [upgradedPlan, setUpgradedPlan] = useState<string | null>(null);

  if (dismissed) return null;

  const currentPlanIndex = PLANS.findIndex(
    (p) => p.name === currentPlan || p.discount === currentDiscount,
  );
  const upgradePlans = PLANS.filter((_, i) => i > currentPlanIndex);

  if (upgradePlans.length === 0) return null;

  const calculateSavingsForPlan = (plan: typeof PLANS[0]) => {
    let discountedTotal = 0;
    let fullPriceTotal = 0;
    let piecesProcessed = 0;

    for (const item of cartItems) {
      for (let q = 0; q < item.quantity; q++) {
        piecesProcessed++;
        if (piecesProcessed <= plan.limit) {
          discountedTotal += Math.round(item.basePrice * (1 - plan.discount / 100));
        } else {
          fullPriceTotal += item.basePrice;
        }
      }
    }

    const newTotal = discountedTotal + fullPriceTotal;

    let currentTotal = 0;
    let currentProcessed = 0;
    for (const item of cartItems) {
      for (let q = 0; q < item.quantity; q++) {
        currentProcessed++;
        if (currentProcessed <= currentLimit) {
          currentTotal += Math.round(item.basePrice * (1 - currentDiscount / 100));
        } else {
          currentTotal += item.basePrice;
        }
      }
    }

    const savings = currentTotal - newTotal;
    const overLimitInNewPlan = Math.max(0, totalItems - plan.limit);

    return { savings, newTotal, overLimitInNewPlan };
  };

  const recommendedPlan =
    upgradePlans.find((p) => p.limit >= totalItems) || upgradePlans[upgradePlans.length - 1];
  const recommended = calculateSavingsForPlan(recommendedPlan);

  const getNetBenefit = (plan: typeof PLANS[0]) => {
    const info = calculateSavingsForPlan(plan);
    const currentPlanPrice = currentPlanIndex >= 0 ? PLANS[currentPlanIndex].price : 0;
    const upgradeCost = plan.price - currentPlanPrice;
    return info.savings - upgradeCost;
  };

  const handleUpgradeInCart = (plan: typeof PLANS[0]) => {
    if (onUpgradeInCart) {
      try {
        const currentPlanPrice = currentPlanIndex >= 0 ? PLANS[currentPlanIndex].price : 0;
        const upgradeCost = plan.price - currentPlanPrice;
        onUpgradeInCart(plan.name, plan.discount, plan.limit, upgradeCost);
        setUpgradedPlan(plan.name);
        setTimeout(() => setDismissed(true), 2500);
      } catch (error) {
        console.error('Upgrade failed:', error);
      }
    }
  };

  const isApproaching = mode === 'approaching-limit';
  const usagePercent = currentLimit > 0 ? Math.round((totalItems / currentLimit) * 100) : 0;

  // Show success state after upgrade
  if (upgradedPlan) {
    const plan = PLANS.find((p) => p.name === upgradedPlan);
    const currentPlanPrice = currentPlanIndex >= 0 ? PLANS[currentPlanIndex].price : 0;
    const upgradeCost = (plan?.price || 0) - currentPlanPrice;
    return (
      <div className="mb-4 sm:mb-6 relative overflow-hidden rounded-xl sm:rounded-2xl border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 via-white to-teal-50 shadow-lg">
        <div className="p-4 sm:p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center flex-shrink-0 animate-bounce">
              <i className="ri-check-line text-white text-xl sm:text-2xl"></i>
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-bold text-emerald-800">
                Upgrade na {upgradedPlan} přidán do košíku!
              </h3>
              <p className="text-xs sm:text-sm text-emerald-600">
                Cena členství {upgradeCost} Kč byla přidána k celkové ceně objednávky.
                Sleva {plan?.discount}% je aktivní.
                {plan?.freeShipping && ' Doprava je nyní ZDARMA.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`mb-4 sm:mb-6 relative overflow-hidden rounded-xl sm:rounded-2xl border-2 ${
        isApproaching
          ? 'border-amber-200 bg-gradient-to-br from-amber-50 via-white to-orange-50'
          : 'border-teal-300 bg-gradient-to-br from-teal-50 via-white to-emerald-50'
      } shadow-lg`}
    >
      {/* Dismiss button */}
      <button
        onClick={() => setDismissed(true)}
        className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-colors cursor-pointer z-10"
        title="Zavřít"
      >
        <i className="ri-close-line text-lg"></i>
      </button>

      <div className="p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-start gap-3 mb-4">
          <div
            className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
              isApproaching
                ? 'bg-gradient-to-br from-amber-500 to-orange-500'
                : 'bg-gradient-to-br from-teal-500 to-emerald-500'
            }`}
          >
            <i
              className={`${
                isApproaching ? 'ri-lightbulb-flash-fill' : 'ri-arrow-up-circle-fill'
              } text-white text-xl sm:text-2xl`}
            ></i>
          </div>
          <div className="flex-1 pr-6">
            {isApproaching ? (
              <>
                <h3 className="text-base sm:text-lg font-bold text-gray-900">
                  Blížíte se k limitu — zvažte upgrade
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 mt-0.5">
                  Využíváte <strong>{usagePercent}%</strong> svého limitu ({totalItems} z{' '}
                  {currentLimit} ks). S upgradem na <strong>{recommendedPlan.name}</strong> získáte
                  více kusů se slevou a nemusíte platit plnou cenu.
                </p>
              </>
            ) : (
              <>
                <h3 className="text-base sm:text-lg font-bold text-gray-900">
                  Upgradujte a zaplaťte vše najednou
                </h3>
                <p className="text-xs sm:text-sm text-gray-600 mt-0.5">
                  Máte <strong>{overLimitPieces} ks</strong> nad limit — za plnou cenu.
                  {recommended.overLimitInNewPlan === 0
                    ? ` S ${recommendedPlan.name} budou všechny kusy se slevou.`
                    : ` S ${recommendedPlan.name} získáte slevu na více kusů.`}
                </p>
              </>
            )}
          </div>
        </div>

        {/* Recommended plan card */}
        <div className="bg-white rounded-xl border-2 border-teal-200 p-4 sm:p-5 mb-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-teal-100 to-emerald-100 rounded-lg flex items-center justify-center">
                <i className="ri-vip-crown-fill text-teal-600 text-lg"></i>
              </div>
              <div>
                <span className="font-bold text-gray-900 text-sm sm:text-base">
                  {recommendedPlan.name}
                </span>
                <span className="text-xs text-gray-500 ml-2">
                  {recommendedPlan.limit} ks / sleva {recommendedPlan.discount}%
                </span>
              </div>
            </div>
            <div className="text-right">
              <span className="text-lg sm:text-xl font-bold text-gray-900">
                {recommendedPlan.price} Kč
              </span>
              <span className="text-xs text-gray-500 block">/ měsíc</span>
            </div>
          </div>

          {/* Savings highlight */}
          {recommended.savings > 0 && (
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg p-3 mb-3 border border-emerald-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className="ri-money-cny-circle-line text-emerald-600 text-lg"></i>
                  <span className="text-sm font-medium text-emerald-800">
                    Úspora na žehlení
                  </span>
                </div>
                <span className="text-lg font-bold text-emerald-600">−{recommended.savings} Kč</span>
              </div>
            </div>
          )}

          {/* Cenový rozpis upgradu v košíku */}
          <div className="bg-gray-50 rounded-lg p-3 mb-3 border border-gray-200">
            <p className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
              <i className="ri-receipt-line text-teal-600"></i>
              Co se přidá do košíku:
            </p>
            <div className="space-y-1 text-xs sm:text-sm">
              <div className="flex justify-between text-gray-600">
                <span>Členství {recommendedPlan.name} (měsíční)</span>
                <span className="font-semibold">
                  +{(() => {
                    const currentPrice =
                      currentPlanIndex >= 0 ? PLANS[currentPlanIndex].price : 0;
                    return recommendedPlan.price - currentPrice;
                  })()}{' '}
                  Kč
                </span>
              </div>
              {recommended.savings > 0 && (
                <div className="flex justify-between text-emerald-600">
                  <span>Úspora na žehlení</span>
                  <span className="font-semibold">−{recommended.savings} Kč</span>
                </div>
              )}
              <div className="flex justify-between text-gray-900 font-bold border-t border-gray-200 pt-1 mt-1">
                <span>Čistý dopad na cenu</span>
                {(() => {
                  const currentPrice = currentPlanIndex >= 0 ? PLANS[currentPlanIndex].price : 0;
                  const upgradeCost = recommendedPlan.price - currentPrice;
                  const net = upgradeCost - recommended.savings;
                  return (
                    <span className={net <= 0 ? 'text-emerald-600' : 'text-gray-900'}>
                      {net <= 0 ? `−${Math.abs(net)}` : `+${net}`} Kč
                    </span>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* Benefits */}
          <div className="grid grid-cols-2 gap-2 mb-4">
            <div className="flex items-center gap-1.5 text-xs sm:text-sm text-gray-700">
              <i className="ri-percent-line text-teal-500"></i>
              <span>Sleva {recommendedPlan.discount}%</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs sm:text-sm text-gray-700">
              <i className="ri-stack-line text-teal-500"></i>
              <span>{recommendedPlan.limit} ks v ceně</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs sm:text-sm text-gray-700">
              <i className="ri-truck-line text-teal-500"></i>
              <span>Dodání do {recommendedPlan.deliveryHours}h</span>
            </div>
            {recommendedPlan.freeShipping && (
              <div className="flex items-center gap-1.5 text-xs sm:text-sm text-teal-700 font-medium">
                <i className="ri-gift-line text-teal-500"></i>
                <span>Doprava ZDARMA</span>
              </div>
            )}
            {recommendedPlan.freeWeekend && (
              <div className="flex items-center gap-1.5 text-xs sm:text-sm text-teal-700 font-medium">
                <i className="ri-calendar-check-line text-teal-500"></i>
                <span>Víkendový svoz ZDARMA</span>
              </div>
            )}
          </div>

          {/* CTA button - upgrade přímo v košíku s platbou */}
          <div className="space-y-2">
            <button
              onClick={() => handleUpgradeInCart(recommendedPlan)}
              className="w-full py-3 bg-gradient-to-r from-teal-600 to-emerald-500 hover:from-teal-700 hover:to-emerald-600 text-white rounded-xl font-semibold text-sm sm:text-base transition-all shadow-md hover:shadow-lg cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
            >
              <i className="ri-shopping-cart-fill text-lg"></i>
              Přidat upgrade do košíku —{' '}
              {(() => {
                const currentPrice = currentPlanIndex >= 0 ? PLANS[currentPlanIndex].price : 0;
                return recommendedPlan.price - currentPrice;
              })()}{' '}
              Kč
            </button>
            <p className="text-[10px] sm:text-xs text-center text-gray-500">
              <i className="ri-information-line mr-0.5"></i>
              Cena členství bude přidána k celkové ceně objednávky a zaplatíte vše najednou
            </p>
          </div>
        </div>

        {/* Show all upgrade options */}
        {upgradePlans.length > 1 && (
          <>
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="w-full text-center text-sm text-teal-600 hover:text-teal-700 font-medium cursor-pointer flex items-center justify-center gap-1 py-1"
            >
              {showDetails
                ? 'Skrýt další plány'
                : `Zobrazit všechny možnosti upgradu (${upgradePlans.length})`}
              <i
                className={`ri-arrow-${showDetails ? 'up' : 'down'}-s-line text-lg`}
              ></i>
            </button>

            {showDetails && (
              <div className="mt-3 space-y-2">
                {upgradePlans.map((plan) => {
                  const info = calculateSavingsForPlan(plan);
                  const isRecommended = plan.name === recommendedPlan.name;
                  if (isRecommended) return null;

                  const currentPlanPrice =
                    currentPlanIndex >= 0 ? PLANS[currentPlanIndex].price : 0;
                  const upgradeCost = plan.price - currentPlanPrice;

                  return (
                    <div
                      key={plan.name}
                      className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4"
                    >
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <i className="ri-vip-crown-line text-gray-600 text-lg"></i>
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-gray-900 text-sm">{plan.name}</span>
                              <span className="text-xs text-gray-500">
                                {plan.limit} ks / −{plan.discount}%
                              </span>
                            </div>
                            {info.savings > 0 && (
                              <span className="text-xs text-emerald-600 font-medium">
                                Ušetříte {info.savings} Kč na žehlení
                              </span>
                            )}
                            {info.overLimitInNewPlan > 0 && (
                              <span className="text-xs text-amber-600 block">
                                Stále {info.overLimitInNewPlan} ks nad limit
                              </span>
                            )}
                          </div>
                        </div>
                        <span className="font-bold text-gray-900 text-sm flex-shrink-0">
                          +{upgradeCost} Kč
                        </span>
                      </div>
                      <div className="ml-11">
                        <button
                          onClick={() => handleUpgradeInCart(plan)}
                          className="w-full px-3 py-2 bg-teal-50 hover:bg-teal-100 text-teal-700 rounded-lg text-xs sm:text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1"
                        >
                          <i className="ri-shopping-cart-fill"></i>
                          Přidat do košíku — {upgradeCost} Kč
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Comparison link */}
        <div className="mt-3 text-center">
          <a
            href="/clenstvi"
            className="text-xs text-gray-500 hover:text-teal-600 underline cursor-pointer"
          >
            Porovnat všechny plány členství
          </a>
        </div>
      </div>
    </div>
  );
}
